/**
 * Current prices.
 *
 * One row per offering, because the same model sold by two companies is two
 * prices. Each row says which source reported it and how recently that source
 * was confirmed, so a number is never shown without its provenance.
 */

import { useQuery } from "@tanstack/react-query";

import type {
  PriceFilterSummary,
  PriceListQuery,
  PriceRow,
  PricingStatistics,
  RateSet,
  TokenLimits,
} from "@pricing/contracts";
import { client } from "../lib/api.ts";
import { contextParams, type ExplorerState, type UpdateState } from "../lib/url-state.ts";
import { CacheWriteCell, RateCell } from "../components/RateCell.tsx";
import { ContextBar } from "../components/ContextBar.tsx";
import { StatsStrip } from "../components/StatsStrip.tsx";
import { SellerSelect } from "../components/SellerSelect.tsx";
import { TextFilter, validateInstant } from "../components/TextFilter.tsx";
import { describeLimits, formatContextBand, formatInstant } from "../lib/format.ts";
import { describeShown, usePagedQuery } from "../lib/paging.ts";

export interface ViewProps {
  readonly state: ExplorerState;
  readonly update: UpdateState;
}

/** Every filter this view sets; "Clear filters" resets exactly these. */
const CLEARED_FILTERS = {
  q: "",
  billing_provider_id: "",
  at_utc: "",
  source_id: "",
  freshness: "",
  min_input_tokens: "",
} as const satisfies Partial<ExplorerState>;

const FILTER_KEYS = Object.keys(CLEARED_FILTERS) as (keyof typeof CLEARED_FILTERS)[];

/** One sentence per reason the capacity filter hid an offering, or null. */
function describeHidden(summary: PriceFilterSummary): string | null {
  const offerings = (count: number) => `${count} offering${count === 1 ? "" : "s"}`;
  const sentences = [
    summary.no_stated_limits > 0
      ? `${offerings(summary.no_stated_limits)} hidden: no source states their limits.`
      : null,
    summary.below_minimum > 0
      ? `${offerings(summary.below_minimum)} hidden: their stated limit is below the selected minimum.`
      : null,
    summary.no_selected_price > 0
      ? `${offerings(summary.no_selected_price)} hidden: no price is selected for them under these filters.`
      : null,
  ].filter((sentence) => sentence !== null);
  return sentences.length > 0 ? sentences.join(" ") : null;
}

export function PricesView({ state, update }: ViewProps): React.JSX.Element {
  const params: PriceListQuery = {
    ...contextParams(state),
    ...(state.q ? { q: state.q } : {}),
    ...(state.billing_provider_id ? { billing_provider_id: state.billing_provider_id } : {}),
    ...(state.at_utc ? { at_utc: state.at_utc } : {}),
    ...(state.freshness === "require_fresh" ? { freshness: state.freshness } : {}),
    ...(state.source_id ? { source_id: state.source_id } : {}),
    ...(state.min_input_tokens ? { min_input_tokens: Number(state.min_input_tokens) } : {}),
    limit: 200,
  };

  const {
    query: prices,
    rows,
    first: envelope,
  } = usePagedQuery<PriceRow, Awaited<ReturnType<typeof client.prices>>>(["prices", params], (cursor) =>
    client.prices(cursor ? { cursor } : params),
  );
  const statistics = useQuery({
    queryKey: ["statistics", contextParams(state), state.billing_provider_id, state.q],
    queryFn: () =>
      client.statistics({
        ...contextParams(state),
        ...(state.q ? { q: state.q } : {}),
        ...(state.billing_provider_id ? { billing_provider_id: state.billing_provider_id } : {}),
      } as never),
  });

  const filtered = FILTER_KEYS.some((key) => state[key] !== "");
  const hidden = envelope?.filter_summary ? describeHidden(envelope.filter_summary) : null;

  return (
    <>
      <section className="panel">
        <h2 className="panel__title">Filters</h2>
        <div className="filters">
          <TextFilter
            label="Search model or alias"
            type="search"
            value={state.q}
            placeholder="claude-sonnet, gpt-4o…"
            onCommit={(value) => update({ q: value }, { replace: true })}
          />
          <SellerSelect
            value={state.billing_provider_id}
            onChange={(value) => update({ billing_provider_id: value })}
          />
          <TextFilter
            label="As of (UTC)"
            value={state.at_utc}
            placeholder="2026-09-01"
            validate={validateInstant}
            onCommit={(value) => update({ at_utc: value }, { replace: true })}
          />
          <label className="field">
            Freshness
            <select
              value={state.freshness}
              onChange={(event) => update({ freshness: event.target.value })}
            >
              <option value="">Allow stale (default)</option>
              <option value="require_fresh">Require fresh</option>
            </select>
          </label>
          <label
            className="field"
            title="Compares maximum input when a source states it; otherwise compares the context window, which counts input and output together."
          >
            Minimum input capacity
            <select
              value={state.min_input_tokens}
              onChange={(event) => update({ min_input_tokens: event.target.value })}
            >
              <option value="">Any</option>
              <option value="32000">32K</option>
              <option value="128000">128K</option>
              <option value="200000">200K</option>
              <option value="400000">400K</option>
              <option value="1000000">1M</option>
            </select>
          </label>
          <a
            className="button"
            href={`/api/catalog-revisions/${envelope?.catalog_revision_id ?? ""}/snapshot`}
            aria-disabled={!envelope?.catalog_revision_id}
          >
            Export this catalog version
          </a>
        </div>
      </section>

      <section className="panel">
        <h2 className="panel__title">
          Offerings{" "}
          <span className="badge">{describeShown(rows.length, prices.hasNextPage, "offering")}</span>
        </h2>

        {prices.isPending ? <p className="notice">Loading prices…</p> : null}

        {hidden ? <p className="notice">{hidden}</p> : null}

        {prices.isError ? (
          // Reported here, under the filters that caused it, rather than in
          // place of the whole view: an as-of time past the catalog's cutoff
          // is a value to correct, and the field to correct it in stays.
          <div className="notice notice--danger" role="alert">
            <p>The prices could not be loaded: {(prices.error as Error).message}</p>
            {filtered ? (
              <button
                type="button"
                className="button"
                style={{ marginTop: "var(--space-3)" }}
                onClick={() => update(CLEARED_FILTERS)}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : !prices.isPending && rows.length === 0 ? (
          filtered ? (
            <div className="notice notice--empty">
              <p>No offering matches these filters.</p>
              <button
                type="button"
                className="button"
                style={{ marginTop: "var(--space-3)" }}
                onClick={() => update(CLEARED_FILTERS)}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <p className="notice notice--empty">
              Nothing has been collected yet. Run a refresh from the Sources view.
            </p>
          )
        ) : null}

        {rows.length > 0 ? (
          <div className="table-wrap">
            <table className="table--sticky-first">
              <caption className="visually-hidden">
                Current rates per million tokens, one row per offering
              </caption>
              <thead>
                <tr>
                  <th scope="col">Model · seller · tier · prompt size</th>
                  <th scope="col" className="numeric">
                    Input
                  </th>
                  <th scope="col" className="numeric">
                    Output
                  </th>
                  <th scope="col" className="numeric">
                    Cache read
                  </th>
                  <th
                    scope="col"
                    className="numeric"
                    title="Every cache-write rate the seller bills: one unsplit rate, or one per cache lifetime"
                  >
                    Cache write
                  </th>
                  <th
                    scope="col"
                    title="Token limits as the source states them: a context window counts input and output together"
                  >
                    Limits
                  </th>
                  <th scope="col">Source</th>
                  <th scope="col" title="When the current price was first observed, and whether its source is within its freshness policy">
                    Price since
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <PriceRowView
                    key={row.offering_id}
                    row={row}
                    onOpen={() => update({ view: "model", offering_id: row.offering_id })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {prices.hasNextPage ? (
          <button
            type="button"
            className="button load-more"
            disabled={prices.isFetchingNextPage}
            onClick={() => void prices.fetchNextPage()}
          >
            {prices.isFetchingNextPage ? "Loading…" : "Show more offerings"}
          </button>
        ) : null}
      </section>

      {/* After the table: the prices are what people come for, and on a phone
          six statistics cards pushed the first price two screens down. */}
      <StatsStrip statistics={(statistics.data?.data ?? null) as PricingStatistics | null} />

      {envelope ? (
        <ContextBar
          catalogRevisionId={envelope.catalog_revision_id}
          observationSequence={envelope.observation_sequence}
          observationCutoffUtc={envelope.observation_cutoff_utc}
          atUtc={(envelope as { at_utc?: string }).at_utc}
          freshness={(envelope as { freshness?: string }).freshness}
          pinned={state.catalog_revision_id !== ""}
          onPin={() =>
            update({
              catalog_revision_id:
                state.catalog_revision_id === "" ? (envelope.catalog_revision_id ?? "") : "",
            })
          }
        />
      ) : null}
    </>
  );
}

function PriceRowView({
  row,
  onOpen,
}: {
  readonly row: PriceRow;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const resolution = row.resolution;
  // The name opens the offering; seller, tier and prompt size ride under it,
  // so the row's identity survives a narrow screen and the rates sit right
  // beside it instead of three columns away.
  const identity = (
    <th scope="row">
      <button type="button" className="link-button" onClick={onOpen}>
        {row.model_display_name}
      </button>
      <div className="stat__denominator">
        {row.offering.billing_provider_id} · {row.offering.service_tier} ·{" "}
        {formatContextBand(row.offering.context_band)}
      </div>
    </th>
  );

  if (resolution.status !== "matched") {
    return (
      <tr>
        {identity}
        <td colSpan={7}>
          <span className="rate rate--unknown">
            no accepted price ({resolution.status === "not_found" ? resolution.reason : resolution.status})
          </span>
        </td>
      </tr>
    );
  }

  const rates: RateSet = resolution.selected.rate_set;
  const stale = resolution.warnings.some((warning) => warning.code === "stale_rate");
  return (
    <tr>
      {identity}
      <td className="numeric">
        <RateCell rate={rates.input} />
      </td>
      <td className="numeric">
        <RateCell rate={rates.output} />
      </td>
      <td className="numeric">
        <RateCell rate={rates.cache_read} />
      </td>
      <td className="numeric">
        <CacheWriteCell rates={rates} />
      </td>
      <td>
        <LimitsCell
          limits={resolution.selected.limits}
          sourceId={resolution.selected.limits_source_id}
          rateSourceId={resolution.selected.source_id}
        />
      </td>
      <td>
        {resolution.selected.source_id}
        {resolution.alternatives.length > 0 ? (
          <>
            {" "}
            <span className="badge" title="Another source reports this offering too">
              +{resolution.alternatives.length}
            </span>
          </>
        ) : null}
      </td>
      <td>
        {/* The date the current price was first observed: not a confirmation,
            which is why the column no longer claims to be one. */}
        <span title={`Current price first observed ${formatInstant(resolution.selected.start_utc)}`}>
          {resolution.selected.start_utc.slice(0, 10)}
        </span>{" "}
        {stale ? (
          <span className="badge badge--stale">stale</span>
        ) : (
          <span className="badge badge--ok">fresh</span>
        )}
      </td>
    </tr>
  );
}

/**
 * The limits the source states, compactly, with the exact numbers and their
 * source on hover. When the rate's source states none and another does, that
 * other source is named, because the two facts then come from different places.
 */
function LimitsCell({
  limits,
  sourceId,
  rateSourceId,
}: {
  readonly limits: TokenLimits | null;
  readonly sourceId: string | null;
  readonly rateSourceId: string;
}): React.JSX.Element {
  const lines = describeLimits(limits);
  if (lines.length === 0) {
    return (
      <span className="rate rate--unknown" title="No source states this model's token limits.">
        not stated
      </span>
    );
  }
  const from = sourceId && sourceId !== rateSourceId ? ` (from ${sourceId})` : "";
  return (
    <span
      className="limits"
      title={`${lines.map((line) => line.long).join("; ")}${sourceId ? `, from ${sourceId}` : ""}`}
    >
      {lines.map((line) => (
        <span key={line.short}>{line.short}</span>
      ))}
      {from ? <span className="stat__denominator">{from.trim()}</span> : null}
    </span>
  );
}
